import { useEffect } from 'react'
import { useNavigate } from 'react-router'
import { useQuery } from '@tanstack/react-query'
import {
  AttendanceTime,
  Badge,
  Button,
  EmptyState,
  InfoBox,
  Toast,
  type AttendanceKind,
} from '@biznomi/ui-react'
import { manualReasonLabel, isAbsenceRecord, attendanceTypeLabel } from '@biznomi/types'
import { useAuth } from '@/lib/auth/store'
import { useOrg } from '@/lib/useOrg'
import { getTodayAttendance, getMyMonthlyStats } from '@/lib/api/attendance'
import { useTagging } from '@/lib/useTagging'
import { useSync, initOfflineSync } from '@/lib/offline/sync'

export default function Home() {
  const navigate = useNavigate()
  const user = useAuth((s) => s.user)
  const { data: org } = useOrg()
  const { phase, outcome, tag, clearOutcome } = useTagging()
  const pending = useSync((s) => s.pending)
  const dropped = useSync((s) => s.dropped)
  const clearDropped = useSync((s) => s.clearDropped)

  useEffect(() => initOfflineSync(), [])

  const { data: today } = useQuery({
    queryKey: ['attendance', 'today'],
    queryFn: async () => {
      const res = await getTodayAttendance()
      if (!res.success) throw new Error(res.error ?? '오늘 기록을 불러오지 못했습니다')
      return res.data ?? []
    },
  })

  const { data: monthly } = useQuery({
    queryKey: ['attendance', 'my-monthly'],
    queryFn: async () => {
      const res = await getMyMonthlyStats()
      return res.success ? (res.data ?? null) : null
    },
    staleTime: 5 * 60 * 1000,
  })

  const busy = phase !== 'idle'

  /*
    출근은 찍었는데 퇴근이 없는 상태를 한 줄로 말한다. 깜빡한 채 하루가 지나면
    나중에 수기 정정으로 넘어가는데, 같은 날 보여 주면 그 일이 줄어든다.

    "미퇴근" 이라고 쓰지 않는다. 그건 판정 단어고, 지금 근무 중인 사람에게 틀린 말이다.
    어제 것도 안 본다 — 어제 미퇴근 표시는 영구 폐기됐다 (라이트 v0.5 결정 1·16).
    오늘 안에서만, 사실만.
  */
  const openCheckin =
    !!today &&
    today.some((r) => r.type === 'CHECKIN' && !isAbsenceRecord(r) && r.manualStatus !== 'PENDING') &&
    !today.some((r) => r.type === 'CHECKOUT' && r.manualStatus !== 'PENDING')

  return (
    <div className="space-y-5">
      <header>
        <p className="text-sm text-slate-500">{org?.name}</p>
        <h1 className="text-xl font-bold">{user?.name}님</h1>
        <p className="mt-1 text-sm text-slate-500">
          {user?.departmentName ?? '부서 미지정'}
          {user?.employeeNumber ? ` · ${user.employeeNumber}` : ''}
        </p>
      </header>

      {/* 전송하지 못한 기록 — 조용히 사라지지 않게 (§원칙 1) */}
      {dropped.length > 0 && (
        <InfoBox tone="danger" title={`전송하지 못한 기록 ${dropped.length}건`}>
          <ul className="mb-2 space-y-1">
            {dropped.map((d) => (
              <li key={d.tag.clientId}>
                {new Date(d.tag.clientTimestamp).toLocaleString('ko-KR')} —{' '}
                {d.stale ? '기간이 지나 자동 전송이 안 됩니다. 수기 등록으로 남겨 주세요.' : d.error}
              </li>
            ))}
          </ul>
          {/*
            🔴 안내가 「수기 등록으로 남겨 주세요」라고 하는데 갈 버튼이 없었다 (9/10 발견).
               NFC 미지원 토스트와 **같은 구멍**이라 같이 닫는다.
            ⚠ 기간이 지난 건(stale)만 수기로 갈 일이다. 일시적 실패는 큐가 다시 보낸다.
          */}
          <div className="flex items-center gap-3">
            {dropped.some((d) => d.stale) && (
              <Button size="sm" onClick={() => navigate('/manual')}>
                수기로 등록하기
              </Button>
            )}
            <button className="text-xs underline" onClick={clearDropped}>
              확인했습니다
            </button>
          </div>
        </InfoBox>
      )}

      {/* 기기가 막힌 상태 — 어디로 가야 하는지 알려준다 */}
      {outcome?.kind === 'needs-device' && (
        <InfoBox tone="warning" title="이 기기로는 기록할 수 없습니다">
          <p className="mb-2">{outcome.message}</p>
          <Button size="sm" onClick={() => navigate('/onboarding/device')}>
            기기 등록하러 가기
          </Button>
        </InfoBox>
      )}

      {/*
        🔴 NFC 를 못 읽는 기기 — 위와 **같은 이유로** 토스트가 아니다 (2026-09-12).
           전에는 3초 토스트로 「수기 등록을 이용해 주세요」만 뜨고 사라졌다.
           말로는 가리키는데 갈 문이 없었고, 그게 사라지기까지 했다.
      */}
      {outcome?.kind === 'needs-manual' && (
        <InfoBox tone="warning" title="이 기기로는 태그를 읽을 수 없습니다">
          {/* 제목이 이미 「읽을 수 없습니다」라고 했다. 본문에서 또 말하지 않는다 */}
          <p className="mb-2">수기로 등록하면 관리자 승인 뒤 기록에 남습니다.</p>
          <Button size="sm" onClick={() => navigate('/manual')}>
            수기로 등록하기
          </Button>
        </InfoBox>
      )}

      {/*
        출근·퇴근 2버튼 상시 노출 — 사용자가 명시적으로 고르고 서버가 역전을 검증한다.
        시스템이 알아서 판단하지 않는다 (DCMC 1-2-1, §4 A 전역 채택).
      */}
      <div className="grid grid-cols-2 gap-3">
        <Button size="lg" className="h-24 flex-col text-base" loading={busy} onClick={() => tag('CHECKIN')}>
          출근
        </Button>
        <Button
          size="lg"
          variant="secondary"
          className="h-24 flex-col text-base"
          loading={busy}
          onClick={() => tag('CHECKOUT')}
        >
          퇴근
        </Button>
      </div>

      {/*
        🔴 한 화면에서 서로 반대로 말하던 자리 (2026-09-12, 캡처 71 을 열어서 나왔다).
           바로 위 노란 박스가 「이 기기로는 태그를 읽을 수 없습니다」라고 해 놓고
           이 줄은 계속 「폰을 대면 기록됩니다」라고 했다. 못 대는데 대라고 한 것이다.
           이번 판에서 고친 것과 **같은 종류** — 화면이 사실과 다른 말을 하는 것.
      */}
      <p className="text-center text-xs text-slate-400">
        {outcome?.kind === 'needs-manual'
          ? '이 기기에서는 수기로만 남길 수 있습니다'
          : phase === 'scanning'
            ? 'NFC 태그에 폰을 대주세요'
            : phase === 'sending'
              ? '기록하는 중…'
              : 'NFC 태그에 폰을 대면 그 시각이 그대로 기록됩니다'}
      </p>

      {pending > 0 && (
        <InfoBox tone="warning">
          전송 대기 중인 기록이 {pending}건 있습니다. 인터넷에 연결되면 자동으로 보냅니다.
        </InfoBox>
      )}

      {/*
        이번 달 현황 (§4 B #8) — 본인 출근일수 하나만.
        직원이 제일 자주 묻는 게 "이번 달 며칠 나왔나" 이고, 그 숫자는 사장이 받는
        엑셀의 출근일수와 같아야 한다. 그래서 세는 규칙을 서버 한 곳으로 맞춰 뒀다.
        근무시간 합계·지각 횟수는 넣지 않는다 — 전자는 휴게시간 해석이 따라오고,
        후자는 판정을 화면 밖으로 꺼내는 일이다.
      */}
      {org?.uiConfig?.showMonthlySummary !== false && monthly && (
        <section className="rounded-card bg-card px-4 py-3.5">
          <div className="flex items-baseline justify-between">
            <h2 className="text-sm text-slate-500">이번 달 출근</h2>
            <p className="text-lg font-bold tabular-nums">
              {monthly.workDays}
              <span className="ml-0.5 text-sm font-normal text-slate-400">일</span>
            </p>
          </div>
          <p className="mt-0.5 text-xs text-slate-400">출근을 찍은 날의 수입니다</p>
        </section>
      )}

      <section>
        <h2 className="mb-2 text-sm font-bold text-slate-500">오늘 기록</h2>
        {openCheckin && (
          <p className="mb-2 text-sm text-slate-500">퇴근은 아직 안 찍혔습니다.</p>
        )}
        {!today || today.length === 0 ? (
          <div className="rounded-card bg-card">
            {/* 위와 같은 이유 — 못 읽는 기기에 「폰을 대면 기록됩니다」는 거짓말이다 */}
            <EmptyState
              title="아직 오늘 기록이 없습니다."
              hint={
                outcome?.kind === 'needs-manual'
                  ? '이 기기에서는 수기로 등록해 주세요.'
                  : 'NFC 태그에 폰을 대면 기록됩니다.'
              }
              icon="clock"
            />
          </div>
        ) : (
          <ul className="divide-y divide-slate-100 overflow-hidden rounded-card bg-card">
            {today.map((r) => {
              // 부재(휴가·출장·교육)를 출퇴근으로 그리면 안 된다. type=ABSENCE 와
              // 옛 형태(type=CHECKIN + manualReason) 를 공용 헬퍼가 함께 커버한다.
              // 이걸 빼먹으면 휴가가 "퇴근 00:00" 으로 보인다 — 실제로 그랬다.
              const absence = isAbsenceRecord(r)
              return (
                <li key={r.id} className="flex items-center justify-between gap-3 px-4 py-3.5">
                  <div className="flex items-center gap-2">
                    {absence ? (
                      <Badge variant="purple">{manualReasonLabel(r.manualReason) || '부재'}</Badge>
                    ) : (
                      <Badge variant={r.type === 'CHECKIN' ? 'info' : 'default'}>
                        {attendanceTypeLabel(r.type)}
                      </Badge>
                    )}
                    {r.isManual && !absence && <Badge variant="outline">수기</Badge>}
                  </div>
                  {absence ? (
                    <span className="text-sm text-slate-400">종일</span>
                  ) : (
                    <AttendanceTime at={new Date(r.timestamp)} kind={r.type as AttendanceKind} />
                  )}
                </li>
              )
            })}
          </ul>
        )}
      </section>

      {/*
        "관리자 화면" 버튼은 뺐다. 하단 «관리» 탭이 같은 /admin 으로 간다 (AppShell 관리 섹션).
        홈은 직원 화면이고 관리자도 여기서는 본인 출퇴근을 찍는다.
        승인 대기처럼 "지금 처리할 게 N건" 이라는 신호가 생기면 그때 다시 올린다.
      */}

      {/* needs-device·needs-manual 은 위에서 InfoBox 로 그린다. 여기서 또 띄우면 겹친다 */}
      {outcome && outcome.kind !== 'needs-device' && outcome.kind !== 'needs-manual' && (
        <Toast
          message={outcome.message}
          variant={outcome.kind === 'saved' ? 'success' : outcome.kind === 'error' ? 'error' : 'warning'}
          duration={outcome.kind === 'queued' ? 6000 : 3000}
          onClose={clearOutcome}
        />
      )}
    </div>
  )
}
